import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import moment from "moment-timezone";
import { CSVLink } from "react-csv";
import * as XLSX from "xlsx";
import PageHeadingNew from "../common-components/custom-comps/PageHeadingNew";
import GymFilter from "../common-components/filters/gym-filter";
import DateRanger from "../components-react/DateRanger";
import CommonTable from "../common-components/common-tables/CommonTable";
import { checkIsManagerRole } from "../common-data/Conversions";
import { BiSearchIcon } from "../../images/icons/icons";
import SelectDropdown from "../components-react/SelectDropdown";
import { fetchTrainerWiseStats, fetchTrainerWiseTotals, fetchTrainerListDropdown, fetchPtTrainerMembers } from "../../data/APIs";
import Modal from "react-modal";
import UserProfile from "../pt-trials/UserProfile";
import ModalHeadAndClose from "../payouts-wtf/common/ModalHeadAndClose";

import "../../styles/unified-pt-view/unified-pt-view.css";

// ─── Static KPI Icons (extracted to avoid re-creation on every render) ───
const KPI_ICONS = {
  trainers: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  check: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  clock: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  cross: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>,
  userPlus: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>,
  trend: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  calendar: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
};

const ChevronIcon = ({ up }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: "transform 0.2s", transform: up ? "rotate(180deg)" : "rotate(0deg)" }}>
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);


const UnifiedPTDashboard = ({ userInfoData }) => {
  const isAdmin = userInfoData?.account_type === "admin";
  const isGym = userInfoData?.account_type === "gym";
  const isManagerRole = checkIsManagerRole(userInfoData);
  const token = userInfoData?.token;

  const [selectedGym, setSelectedGym] = useState(
    !isAdmin && !isManagerRole
      ? {
          label: isGym ? userInfoData?.name : userInfoData?.gym_name || "",
          value: userInfoData?.gym_id || userInfoData?.user_id || userInfoData?.uid || "",
        }
      : null
  );

  const [showFilters, setShowFilters] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);

  const [searchString, setSearchString] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [searchApplied, setSearchApplied] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [trainerOptions, setTrainerOptions] = useState([]);
  const [activeKpi, setActiveKpi] = useState(null); // field name of active KPI card filter
  const [dateRangerOpen, setDateRangerOpen] = useState(false);
  const initialRangeState = useMemo(() => [{
    startDate: new Date(),
    endDate: moment().add(30, "days").toDate(),
    key: "selection",
  }], []);
  const [rangeState, setRangeState] = useState(initialRangeState);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const gymId = selectedGym?.value || "";

  // ─── Member detail modal state ───
  const [selectedMember, setSelectedMember] = useState(null);
  const closeMemberModal = useCallback(() => setSelectedMember(null), []);

  // ─── Drill-down state ───
  const [detailView, setDetailView] = useState(null); // { trainerName, trainerId, title, drillType }
  const [drillData, setDrillData] = useState([]);
  const [isDrillLoading, setIsDrillLoading] = useState(false);
  const [drillPage, setDrillPage] = useState(0);
  const [drillPageLimit, setDrillPageLimit] = useState(25);
  const [drillTotalRecords, setDrillTotalRecords] = useState(0);

  const openDetail = useCallback((trainerId, trainerName, title, drillType) => {
    setDetailView({ trainerName, trainerId, title, drillType: drillType || "all" });
    setDrillPage(0);
    setDrillData([]);
  }, []);

  const closeDetail = useCallback(() => {
    setDetailView(null);
  }, []);


  // ─── Server-fetched KPI stats ───
  const [kpiStats, setKpiStats] = useState({
    totalTrainers: 0, totalPt: 0, totalTrialDone: 0, totalTrialPending: 0,
    totalProspects: 0, totalTrialPtProspect: 0, totalConversions: 0,
    todayTrials: 0, totalTrialMembers: 0, totalRegularMembers: 0,
  });
  const [isKpiLoading, setIsKpiLoading] = useState(false);

  // ─── Build common filter params (shared by stats, totals, and exports) ───
  const buildFilterParams = useCallback(() => {
    const params = { gym_id: gymId, date_from: dateFrom, date_to: dateTo, search: activeSearch };
    if (selectedTrainer?.value) params.trainer_id = selectedTrainer.value;
    if (activeKpi) params.kpi_filter = activeKpi;
    return params;
  }, [gymId, dateFrom, dateTo, activeSearch, selectedTrainer, activeKpi]);

  // ─── Fetch trainer stats table (paginated) ───
  const fetchData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const params = {
        ...buildFilterParams(),
        page: isPage + 1,
        limit: pageLimit,
      };
      const res = await fetchTrainerWiseStats(params, token);

      if (res?.status && Array.isArray(res.data)) {
        const page = res.pagination?.page || 1;
        const limit = res.pagination?.limit || pageLimit;
        res.data.forEach((row, idx) => {
          row._sno = (page - 1) * limit + idx + 1;
        });
        setTableData(res.data);
        setTotalRecords(res.pagination?.total || res.data.length);
      } else {
        setTableData([]);
        setTotalRecords(0);
      }
    } catch (err) {
      console.error("Failed to fetch trainer data:", err);
      setTableData([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [token, buildFilterParams, isPage, pageLimit]);

  // ─── Fetch global KPI totals (NOT called on page change) ───
  const fetchTotals = useCallback(async () => {
    if (!token) return;
    setIsKpiLoading(true);
    try {
      const params = buildFilterParams();
      const res = await fetchTrainerWiseTotals(params, token);

      if (res?.status && res.data) {
        const d = res.data;
        setKpiStats({
          totalTrainers: d.total_trainers || 0,
          totalPt: d.total_pt || 0,
          totalTrialDone: d.total_trial_done || 0,
          totalTrialPending: d.total_trial_pending || 0,
          totalProspects: d.total_prospects || 0,
          totalTrialPtProspect: d.total_trial_pt_prospect || 0,
          totalConversions: d.total_conversions || 0,
          todayTrials: d.today_trials || 0,
          totalTrialMembers: d.total_trial_members || 0,
          totalRegularMembers: d.total_regular_members || 0,
        });
      }
    } catch (err) {
      console.error("Failed to fetch KPI totals:", err);
    } finally {
      setIsKpiLoading(false);
    }
  }, [token, buildFilterParams]);

  // ─── Fetch complete trainer list for dropdown ───
  const fetchTrainerOptions = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetchTrainerListDropdown(gymId, token);
      if (res?.status && Array.isArray(res.data)) {
        const options = res.data
          .map((t) => ({ value: t.trainer_id, label: t.trainer_name }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setTrainerOptions(options);
      } else {
        setTrainerOptions([]);
      }
    } catch (err) {
      console.error("Failed to fetch trainer list:", err);
      setTrainerOptions([]);
    }
  }, [token, gymId]);

  // Trigger table data fetch on any filter or pagination change
  useEffect(() => { fetchData(); }, [fetchData]);

  // Trigger KPI totals fetch on filter change (but NOT on pagination change)
  useEffect(() => { fetchTotals(); }, [fetchTotals]);

  // Trigger trainer dropdown refresh on gym change (or initial mount)
  useEffect(() => { fetchTrainerOptions(); }, [fetchTrainerOptions]);

  // ─── Fetch drill-down members for a specific trainer + type ───
  const fetchDrillData = useCallback(async () => {
    if (!token || !detailView) return;
    setIsDrillLoading(true);
    try {
      const params = {
        trainer_id: detailView.trainerId,
        type: detailView.drillType,
        gym_id: gymId,
        date_from: dateFrom || "",
        date_to: dateTo || "",
        page: drillPage + 1,
        limit: drillPageLimit,
      };
      const res = await fetchPtTrainerMembers(params, token);
      if (res?.status && Array.isArray(res.data)) {
        const page = res.pagination?.page || 1;
        const limit = res.pagination?.limit || drillPageLimit;
        res.data.forEach((row, idx) => { row._sno = (page - 1) * limit + idx + 1; });
        setDrillData(res.data);
        setDrillTotalRecords(res.pagination?.total || res.data.length);
      } else {
        setDrillData([]);
        setDrillTotalRecords(0);
      }
    } catch (err) {
      console.error("Failed to fetch drill-down data:", err);
      setDrillData([]);
      setDrillTotalRecords(0);
    } finally {
      setIsDrillLoading(false);
    }
  }, [token, detailView, gymId, dateFrom, dateTo, drillPage, drillPageLimit]);

  useEffect(() => { fetchDrillData(); }, [fetchDrillData]);

  // When gym changes, reset trainer selection
  const handleGymChange = useCallback((val) => {
    setSelectedGym(val);
    setSelectedTrainer(null);
    setIsPage(0);
  }, []);

  // Server-side pagination
  const tablePagination = useMemo(() => {
    if (totalRecords <= 0) return null;
    const totalPages = Math.ceil(totalRecords / pageLimit) || 1;
    return [{ pages: totalPages, pagination: totalRecords }];
  }, [totalRecords, pageLimit]);

  // Handlers
  const handleSearch = useCallback(() => {
    const trimmed = searchString.trim();
    if (trimmed) {
      setSearchApplied(true);
      setActiveSearch(trimmed);
      setIsPage(0);
    } else if (searchApplied) {
      setSearchApplied(false);
      setActiveSearch("");
      setIsPage(0);
    }
  }, [searchString, searchApplied]);

  const handleKeyDown = useCallback((e) => { if (e.key === "Enter") handleSearch(); }, [handleSearch]);

  useEffect(() => {
    if (!searchString && searchApplied) { setSearchApplied(false); setActiveSearch(""); }
  }, [searchString]);

  const handleResetFilters = useCallback(() => {
    setSearchString(""); setSearchApplied(false); setActiveSearch("");
    setDateFrom(""); setDateTo(""); setRangeState(initialRangeState); setSelectedTrainer(null); setActiveKpi(null);
    if (isAdmin || isManagerRole) setSelectedGym(null);
    setIsPage(0);
  }, [isAdmin, isManagerRole, initialRangeState]);

  const handleDateRangeChange = useCallback((item) => {
    setRangeState([item?.selection]);
    const start = moment(item?.selection?.startDate).tz("Asia/Kolkata").format("YYYY-MM-DD");
    const end = moment(item?.selection?.endDate).tz("Asia/Kolkata").format("YYYY-MM-DD");
    setDateFrom(start);
    setDateTo(end);
    setIsPage(0);
  }, []);

  const clearDateRange = useCallback(() => {
    setDateRangerOpen(false);
    setRangeState(initialRangeState);
    setDateFrom("");
    setDateTo("");
    setIsPage(0);
  }, [initialRangeState]);

  const activeFilterCount = useMemo(() => [!!gymId, !!dateFrom, !!activeSearch, !!selectedTrainer?.value, !!activeKpi].filter(Boolean).length, [gymId, dateFrom, activeSearch, selectedTrainer, activeKpi]);

  // Memoized styles for SelectDropdown to avoid re-renders
  const selectDropdownStyles = useMemo(() => ({
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base, state) => ({
      ...base,
      borderColor: selectedTrainer ? "#28a745" : "#ced4da",
      borderWidth: selectedTrainer ? "2px" : "1px",
      borderStyle: "solid",
      "&:hover": { borderColor: selectedTrainer ? "#28a745" : "#ced4da" },
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0, 0, 0, 0.25)" : "none",
      borderRadius: "4px",
    }),
  }), [selectedTrainer]);

  // Trainer Table Columns — each stat is clickable to show member detail
  const columns = useMemo(() => [
    {
      dataField: "_sno",
      text: "S.No",
      centerAligned: true,
      formatter: (_, row) => (
        <span className="tb-data">{row?._sno || ""}</span>
      ),
    },
    {
      dataField: "trainer_name",
      text: "Trainer Name",
      formatter: (_, row) => (
        <div className="upt-member-cell upt-clickable" onClick={() => openDetail(row?.trainer_id, row?.trainer_name, "All Members", "all")}>
          <div className="upt-member-avatar" style={{ background: "#4F46E5" }}>
            {(row?.trainer_name || "?")[0]?.toUpperCase()}
          </div>
          <div className="upt-member-info">
            <span className="upt-member-name">{row?.trainer_name || "NA"}</span>
            {row?.gym_name && <span className="upt-member-phone">{row.gym_name}</span>}
          </div>
        </div>
      ),
    },
    {
      dataField: "trainer_checkin",
      text: "Check In",
      centerAligned: true,
      formatter: (_, row) => (
        <span className={`upt-status-badge ${row?.trainer_checkin ? "upt-status--active" : "upt-status--pending"}`}>
          {row?.trainer_checkin || "—"}
        </span>
      ),
    },
    {
      dataField: "trainer_checkout",
      text: "Check Out",
      centerAligned: true,
      formatter: (_, row) => (
        <span className={`upt-status-badge ${row?.trainer_checkout ? "upt-status--active" : "upt-status--pending"}`}>
          {row?.trainer_checkout || "—"}
        </span>
      ),
    },
    {
      dataField: "trial_members_assigned",
      text: "Trial Members",
      centerAligned: true,
      formatter: (_, row) => (
        <span className="upt-stat-value upt-clickable" onClick={() => openDetail(row?.trainer_id, row?.trainer_name, "Trial Members Assigned", "all")}>
          {row?.trial_members_assigned || 0}
        </span>
      ),
    },
    {
      dataField: "no_of_pt",
      text: "No. of PT",
      centerAligned: true,
      formatter: (_, row) => (
        <span className="upt-stat-value upt-stat--primary upt-clickable" onClick={() => openDetail(row?.trainer_id, row?.trainer_name, "PT Members", "no_of_pt")}>
          {row?.no_of_pt || 0}
        </span>
      ),
    },
    {
      dataField: "regular_members",
      text: "Regular Members",
      centerAligned: true,
      formatter: (_, row) => (
        <span className="upt-stat-value upt-stat--blue upt-clickable" onClick={() => openDetail(row?.trainer_id, row?.trainer_name, "Regular Members", "regular_members")}>
          {row?.regular_members || 0}
        </span>
      ),
    },
    {
      dataField: "trial_done",
      text: "Trial Done",
      centerAligned: true,
      formatter: (_, row) => (
        <span className="upt-ratio-badge upt-ratio--green upt-clickable" onClick={() => openDetail(row?.trainer_id, row?.trainer_name, "Trial Done", "trial_done")}>
          {row?.trial_done || 0}/{row?.trial_total || 0}
        </span>
      ),
    },
    {
      dataField: "trial_pending",
      text: "Trial Pending",
      centerAligned: true,
      formatter: (_, row) => (
        <span className="upt-ratio-badge upt-ratio--amber upt-clickable" onClick={() => openDetail(row?.trainer_id, row?.trainer_name, "Trial Pending", "trial_pending")}>
          {row?.trial_pending || 0}/{row?.trial_pending_total || 0}
        </span>
      ),
    },
    {
      dataField: "pt_prospect",
      text: "PT Prospect",
      centerAligned: true,
      formatter: (_, row) => (
        <span className="upt-stat-value upt-stat--blue upt-clickable" onClick={() => openDetail(row?.trainer_id, row?.trainer_name, "PT Prospect", "pt_prospect")}>
          {row?.pt_prospect || 0}
        </span>
      ),
    },
    {
      dataField: "trial_pt_prospect",
      text: "Trial PT Prospect",
      centerAligned: true,
      formatter: (_, row) => (
        <span className="upt-stat-value upt-stat--purple upt-clickable" onClick={() => openDetail(row?.trainer_id, row?.trainer_name, "Trial PT Prospect", "trial_pt_prospect")}>
          {row?.trial_pt_prospect || 0}
        </span>
      ),
    },
    {
      dataField: "pt_conversion",
      text: "PT Conversion",
      centerAligned: true,
      formatter: (_, row) => (
        <span className="upt-stat-value upt-stat--green upt-clickable" onClick={() => openDetail(row?.trainer_id, row?.trainer_name, "PT Conversion", "pt_conversion")}>
          {row?.pt_conversion || 0}
        </span>
      ),
    },
    {
      dataField: "bca_done",
      text: "BCA Done",
      centerAligned: true,
      formatter: (_, row) => (
        <span className="upt-stat-value upt-stat--green upt-clickable" onClick={() => openDetail(row?.trainer_id, row?.trainer_name, "BCA Done", "bca_done")}>
          {row?.bca_done || 0}
        </span>
      ),
    },
    {
      dataField: "bca_pending",
      text: "BCA Pending",
      centerAligned: true,
      formatter: (_, row) => (
        <span className="upt-stat-value upt-stat--amber upt-clickable" onClick={() => openDetail(row?.trainer_id, row?.trainer_name, "BCA Pending", "bca_pending")}>
          {row?.bca_pending || 0}
        </span>
      ),
    },
    {
      dataField: "today_trial",
      text: "Today Trial",
      centerAligned: true,
      formatter: (_, row) => {
        const count = row?.today_trial || 0;
        return (
          <span className={`upt-today-badge ${count > 0 ? "upt-today--active" : ""} upt-clickable`} onClick={() => openDetail(row?.trainer_id, row?.trainer_name, "Today's Trials", "today_trial")}>
            {count}
          </span>
        );
      },
    },
  ], [openDetail]);

  // Drill-down member columns — different columns for no_of_pt vs other types
  const drillColumnsPt = useMemo(() => [
    {
      dataField: "_sno", text: "S.No", centerAligned: true,
      formatter: (_, row) => <span className="tb-data">{row?._sno || ""}</span>,
    },
    {
      dataField: "user_name", text: "Member Name",
      formatter: (_, row) => (
        <div className="upt-member-cell upt-clickable" onClick={() => setSelectedMember(row)}>
          <div className="upt-member-avatar" style={{ background: "#4F46E5" }}>
            {(row?.user_name || "?")[0]?.toUpperCase()}
          </div>
          <div className="upt-member-info">
            <span className="upt-member-name">{row?.user_name || "NA"}</span>
            {row?.user_mobile && <span className="upt-member-phone">{row.user_mobile}</span>}
          </div>
        </div>
      ),
    },
    {
      dataField: "member_checkin", text: "Check In", centerAligned: true,
      formatter: (_, row) => (
        <span className={`upt-status-badge ${row?.member_checkin ? "upt-status--active" : "upt-status--pending"}`}>
          {row?.member_checkin || "—"}
        </span>
      ),
    },
    {
      dataField: "member_checkout", text: "Check Out", centerAligned: true,
      formatter: (_, row) => (
        <span className={`upt-status-badge ${row?.member_checkout ? "upt-status--active" : "upt-status--pending"}`}>
          {row?.member_checkout || "—"}
        </span>
      ),
    },
    {
      dataField: "gym_name", text: "Gym", centerAligned: true,
      formatter: (_, row) => <span className="tb-data">{row?.gym_name || "NA"}</span>,
    },
    {
      dataField: "current_plan_name", text: "Plan Name", centerAligned: true,
      formatter: (_, row) => <span className="tb-data">{row?.current_plan_name || "NA"}</span>,
    },
    {
      dataField: "n_session", text: "Total Sessions", centerAligned: true,
      formatter: (_, row) => <span className="tb-data">{row?.n_session || 0}</span>,
    },
    {
      dataField: "consumed_session", text: "Consumed", centerAligned: true,
      formatter: (_, row) => <span className="tb-data">{row?.consumed_session || 0}</span>,
    },
    {
      dataField: "start_date", text: "Start Date", centerAligned: true,
      formatter: (_, row) => <span className="tb-data">{row?.start_date ? moment(row.start_date).format("DD MMM YYYY") : "NA"}</span>,
    },
    {
      dataField: "expire_date", text: "End Date", centerAligned: true,
      formatter: (_, row) => <span className="tb-data">{row?.expire_date ? moment(row.expire_date).format("DD MMM YYYY") : "NA"}</span>,
    },
    {
      dataField: "pt_type", text: "PT Type", centerAligned: true,
      formatter: (_, row) => (
        <span className={`upt-status-badge ${row?.pt_type === "paid" ? "upt-status--active" : "upt-status--pending"}`}>
          {row?.pt_type || "NA"}
        </span>
      ),
    },
  ], []);

  const drillColumnsGeneral = useMemo(() => [
    {
      dataField: "_sno", text: "S.No", centerAligned: true,
      formatter: (_, row) => <span className="tb-data">{row?._sno || ""}</span>,
    },
    {
      dataField: "user_name", text: "Member Name",
      formatter: (_, row) => (
        <div className="upt-member-cell upt-clickable" onClick={() => setSelectedMember(row)}>
          <div className="upt-member-avatar" style={{ background: "#4F46E5" }}>
            {(row?.user_name || "?")[0]?.toUpperCase()}
          </div>
          <div className="upt-member-info">
            <span className="upt-member-name">{row?.user_name || "NA"}</span>
            {row?.user_mobile && <span className="upt-member-phone">{row.user_mobile}</span>}
          </div>
        </div>
      ),
    },
    {
      dataField: "member_checkin", text: "Check In", centerAligned: true,
      formatter: (_, row) => (
        <span className={`upt-status-badge ${row?.member_checkin ? "upt-status--active" : "upt-status--pending"}`}>
          {row?.member_checkin || "—"}
        </span>
      ),
    },
    {
      dataField: "member_checkout", text: "Check Out", centerAligned: true,
      formatter: (_, row) => (
        <span className={`upt-status-badge ${row?.member_checkout ? "upt-status--active" : "upt-status--pending"}`}>
          {row?.member_checkout || "—"}
        </span>
      ),
    },
    {
      dataField: "gym_name", text: "Gym", centerAligned: true,
      formatter: (_, row) => <span className="tb-data">{row?.gym_name || "NA"}</span>,
    },
    {
      dataField: "current_plan_name", text: "Subscription", centerAligned: true,
      formatter: (_, row) => <span className="tb-data">{row?.current_plan_name || "NA"}</span>,
    },
    {
      dataField: "start_date", text: "Start Date", centerAligned: true,
      formatter: (_, row) => <span className="tb-data">{row?.start_date ? moment(row.start_date).format("DD MMM YYYY") : "NA"}</span>,
    },
    {
      dataField: "expire_date", text: "End Date", centerAligned: true,
      formatter: (_, row) => <span className="tb-data">{row?.expire_date ? moment(row.expire_date).format("DD MMM YYYY") : "NA"}</span>,
    },
    {
      dataField: "trial_status", text: "Trial Status", centerAligned: true,
      formatter: (_, row) => (
        <span className={`upt-status-badge upt-status--${(row?.trial_status || "").toLowerCase()}`}>
          {row?.trial_status || "NA"}
        </span>
      ),
    },
    {
      dataField: "handover_status", text: "Handover", centerAligned: true,
      formatter: (_, row) => (
        <span className={`upt-status-badge upt-status--${row?.handover_status === "handover_completed" ? "active" : "pending"}`}>
          {row?.handover_status ? row.handover_status.replace(/_/g, " ") : "NA"}
        </span>
      ),
    },
    {
      dataField: "pt_schedule_date", text: "Schedule Date", centerAligned: true,
      formatter: (_, row) => <span className="tb-data">{row?.pt_schedule_date ? moment(row.pt_schedule_date).format("DD MMM YYYY") : "NA"}</span>,
    },
  ], []);

  const drillColumns = detailView?.drillType === "no_of_pt" ? drillColumnsPt : drillColumnsGeneral;

  // Export data
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [allExportData, setAllExportData] = useState([]);
  const exportRef = useRef(null);
  const csvLinkRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) setShowExportMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Format rows for export
  const formatExportRows = (data) => {
    return data.map((row, i) => ({
      "S.No": row._sno || i + 1,
      "Trainer Name": row?.trainer_name || "NA",
      "Gym": row?.gym_name || "NA",
      "Check In": row?.trainer_checkin || "—",
      "Check Out": row?.trainer_checkout || "—",
      "Trial Members Assigned": row?.trial_members_assigned || 0,
      "No. of PT": row?.no_of_pt || 0,
      "Regular Members": row?.regular_members || 0,
      "Trial Done": `${row?.trial_done || 0}/${row?.trial_total || 0}`,
      "Trial Pending": `${row?.trial_pending || 0}/${row?.trial_pending_total || 0}`,
      "PT Prospect": row?.pt_prospect || 0,
      "Trial PT Prospect": row?.trial_pt_prospect || 0,
      "PT Conversion": row?.pt_conversion || 0,
      "BCA Done": row?.bca_done || 0,
      "BCA Pending": row?.bca_pending || 0,
      "Today Trial": row?.today_trial || 0,
    }));
  };

  const csvHeaders = useMemo(() => {
    const keys = ["S.No", "Trainer Name", "Gym", "Check In", "Check Out", "Trial Members Assigned", "No. of PT", "Regular Members", "Trial Done", "Trial Pending", "PT Prospect", "Trial PT Prospect", "PT Conversion", "BCA Done", "BCA Pending", "Today Trial"];
    return keys.map((key) => ({ label: key, key }));
  }, []);

  // For export, fetch ALL trainers (no pagination) to export complete data
  const handleCSVExport = async () => {
    setIsExporting(true);
    try {
      const exportParams = { ...buildFilterParams(), page: 1, limit: 5000 };
      const res = await fetchTrainerWiseStats(exportParams, token);
      const allData = res?.status && Array.isArray(res.data) ? res.data : tableData;
      allData.forEach((row, i) => { row._sno = i + 1; });
      const rows = formatExportRows(allData);
      setAllExportData(rows);
      setTimeout(() => {
        if (csvLinkRef.current) csvLinkRef.current.link.click();
        setShowExportMenu(false);
        setIsExporting(false);
      }, 100);
    } catch {
      setIsExporting(false);
    }
  };

  const handleExcelExport = async () => {
    setIsExporting(true);
    try {
      const exportParams = { ...buildFilterParams(), page: 1, limit: 5000 };
      const res = await fetchTrainerWiseStats(exportParams, token);
      const allData = res?.status && Array.isArray(res.data) ? res.data : tableData;
      allData.forEach((row, i) => { row._sno = i + 1; });
      const exportRows = formatExportRows(allData);
      if (exportRows.length === 0) { setIsExporting(false); return; }
      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "PT Trainers");
      const colWidths = Object.keys(exportRows[0]).map((key) => ({
        wch: Math.max(key.length, ...exportRows.map((r) => String(r[key] || "").length)) + 2,
      }));
      ws["!cols"] = colWidths;
      const fileName = `Unified_PT_Trainers_${moment().format("YYYY-MM-DD")}.xlsx`;
      XLSX.writeFile(wb, fileName);
      setShowExportMenu(false);
    } finally {
      setIsExporting(false);
    }
  };

  const handleKpiClick = useCallback((field) => {
    setActiveKpi((prev) => prev === field ? null : field);
    setIsPage(0);
  }, []);

  return (
    <>
      <Row>
        <Col>
          <PageHeadingNew
            heading="Unified PT View"
            subheading="Trainer-wise PT performance overview"
            onReload={() => { fetchData(); fetchTotals(); }}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
          />
        </Col>
      </Row>

      {/* KPI Summary Cards — clickable to filter table */}
      <div className="upt-kpi-cards">
        {[
          { field: null, value: kpiStats.totalTrainers, label: "Total Trainers", bg: "#EFF6FF", color: "#2563EB", icon: KPI_ICONS.trainers },
          { field: "no_of_pt", value: kpiStats.totalPt, label: "Total PT Members", bg: "#F0FDF4", color: "#16A34A", icon: KPI_ICONS.check },
          { field: "regular_members", value: kpiStats.totalRegularMembers, label: "Regular Members", bg: "#EEF2FF", color: "#4F46E5", icon: KPI_ICONS.trainers },
          { field: "trial_members_assigned", value: kpiStats.totalTrialMembers, label: "Trial Members", bg: "#FFFBEB", color: "#D97706", icon: KPI_ICONS.clock },
          { field: "trial_done", value: kpiStats.totalTrialDone, label: "Trials Done", bg: "#F0FDF4", color: "#16A34A", icon: KPI_ICONS.check },
          { field: "trial_pending", value: kpiStats.totalTrialPending, label: "Trials Pending", bg: "#FEF2F2", color: "#DC2626", icon: KPI_ICONS.cross },
          { field: "pt_prospect", value: kpiStats.totalProspects, label: "PT Prospects", bg: "#EEF2FF", color: "#4F46E5", icon: KPI_ICONS.userPlus },
          { field: "pt_conversion", value: kpiStats.totalConversions, label: "PT Conversions", bg: "#ECFDF5", color: "#059669", icon: KPI_ICONS.trend },
          { field: "today_trial", value: kpiStats.todayTrials, label: "Today's Trials", bg: "#FFF7ED", color: "#EA580C", icon: KPI_ICONS.calendar },
        ].map((card) => (
          <div
            key={card.label}
            className={`upt-kpi-card ${activeKpi === card.field && card.field ? "upt-kpi-card--selected" : ""} ${!card.field ? "" : "upt-kpi-card--clickable"}`}
            onClick={() => card.field && handleKpiClick(card.field)}
            style={activeKpi === card.field && card.field ? { borderColor: card.color, boxShadow: `0 0 0 2px ${card.color}33` } : undefined}
          >
            <div className="upt-kpi-icon" style={{ background: card.bg, color: card.color }}>{card.icon}</div>
            <div className="upt-kpi-content">
              <span className="upt-kpi-value">{card.value}</span>
              <span className="upt-kpi-label">{card.label}</span>
            </div>
            {activeKpi === card.field && card.field && (
              <span className="upt-kpi-active-dot" style={{ background: card.color }} />
            )}
          </div>
        ))}
      </div>

      {/* Filters Bar */}
      <div className="upt-filters-bar">
        <div className="upt-filters-bar-top">
          <h5 className="upt-filter-heading" onClick={() => setShowFilters((prev) => !prev)} style={{ cursor: "pointer", userSelect: "none", margin: 0 }}>
            Filters
            {activeFilterCount > 0 && <span className="upt-filter-count">{activeFilterCount}</span>}
            <ChevronIcon up={showFilters} />
          </h5>
          {activeFilterCount > 0 && (
            <button className="upt-clear-filters-btn" onClick={handleResetFilters}>Clear All</button>
          )}
        </div>

        {showFilters && (
          <div className="upt-filters-bar-content">
            <div className="upt-filters-row">
              <div className="upt-filter-item upt-filter-item--gym">
                <GymFilter selectedGym={selectedGym} setSelectedGym={handleGymChange} />
              </div>
              <div className="upt-filter-item upt-filter-item--daterange">
                <div className={`upt-daterange ${dateFrom || dateTo ? "upt-daterange--active" : ""}`}>
                  <button type="button" className="upt-daterange-trigger" onClick={() => setDateRangerOpen(true)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    {dateFrom && dateTo ? (
                      <span className="upt-daterange-display">
                        {moment(dateFrom).format("DD MMM YYYY")} - {moment(dateTo).format("DD MMM YYYY")}
                      </span>
                    ) : (
                      <span className="upt-daterange-placeholder">Select Date Range</span>
                    )}
                  </button>
                  {(dateFrom || dateTo) && (
                    <button type="button" className="upt-daterange-clear" title="Clear dates" onClick={clearDateRange}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  )}
                </div>
                {dateRangerOpen && (
                  <DateRanger
                    handleDateRangeChange={handleDateRangeChange}
                    rangeState={rangeState}
                    closeRanger={() => setDateRangerOpen(false)}
                    onClose={() => setDateRangerOpen(false)}
                    onClear={clearDateRange}
                  />
                )}
              </div>
              <div className="upt-filter-item upt-filter-item--trainer">
                <div className={`timesheet_dropdown_wrapper ${!selectedTrainer ? "empty-selection" : "has-selection"}`}>
                  <SelectDropdown
                    options={trainerOptions}
                    name="Trainer"
                    selectedOption={selectedTrainer}
                    setSelectedOption={(val) => { setSelectedTrainer(val); setIsPage(0); }}
                    isDisable={false}
                    preventKeyboardOpen={false}
                    menuPortalTarget={document.body}
                    isClearable={true}
                    styles={selectDropdownStyles}
                  />
                </div>
              </div>
              <div className="upt-filter-item upt-filter-item--search">
                <div className={`upt-searchbar ${searchApplied ? "upt-searchbar--active" : ""}`}>
                  <input type="search" className="upt-searchbar-input" placeholder="Search trainer..." value={searchString} onChange={(e) => setSearchString(e.target.value)} onKeyDown={handleKeyDown} />
                  <button type="button" className="upt-searchbar-btn" onClick={handleSearch}><BiSearchIcon /></button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Show either the trainer table OR the drill-down member list */}
      {detailView ? (
        <div className="upt-inline-detail">
          <div className="upt-inline-detail-header">
            <button className="upt-inline-detail-back" onClick={closeDetail}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              Back to Trainers
            </button>
            <div className="upt-inline-detail-info">
              <h4 className="upt-inline-detail-title">{detailView.title}</h4>
              <span className="upt-inline-detail-subtitle">{detailView.trainerName}</span>
            </div>
          </div>
          <div className="upt-inline-detail-body">
            <div className="upt-table-toolbar">
              <div className="upt-table-toolbar-left">
                <span className="upt-table-toolbar-count">
                  Showing <strong>{drillData.length}</strong> of <strong>{drillTotalRecords}</strong> members
                </span>
              </div>
            </div>
            <div className="common-table-container tb-bordered mt15">
              <CommonTable
                tableData={drillData}
                tablePagination={drillTotalRecords > 0 ? [{ pages: Math.ceil(drillTotalRecords / drillPageLimit) || 1, pagination: drillTotalRecords }] : null}
                tableColumns={drillColumns}
                isPage={drillPage}
                setIsPage={setDrillPage}
                pageLimit={drillPageLimit}
                setPageLimit={(val) => { setDrillPageLimit(val); setDrillPage(0); }}
                isLoading={isDrillLoading}
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Table Header Bar with record count + export */}
          <div className="upt-table-toolbar">
            <div className="upt-table-toolbar-left">
              <span className="upt-table-toolbar-count">
                Showing <strong>{tableData.length}</strong> of <strong>{totalRecords}</strong> trainers
                {activeFilterCount > 0 && <span className="upt-table-toolbar-filtered"> (filtered)</span>}
              </span>
            </div>
            <div className="upt-table-toolbar-right">
              <div className="upt-export-wrap" ref={exportRef}>
                <button type="button" className="upt-export-btn" onClick={() => setShowExportMenu((v) => !v)} disabled={totalRecords === 0 || isExporting}>
                  {isExporting ? (
                    <span className="upt-export-spinner" />
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                  )}
                  {isExporting ? "Exporting..." : "Export All"}
                  {!isExporting && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  )}
                </button>
                <CSVLink
                  ref={csvLinkRef}
                  data={allExportData}
                  headers={csvHeaders}
                  filename={`Unified_PT_Trainers_${moment().format("YYYY-MM-DD")}.csv`}
                  className="upt-export-hidden-link"
                />
                {showExportMenu && (
                  <div className="upt-export-menu">
                    <button type="button" className="upt-export-menu-item" onClick={handleCSVExport} disabled={isExporting}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                      </svg>
                      Export as CSV ({totalRecords} trainers)
                    </button>
                    <button type="button" className="upt-export-menu-item" onClick={handleExcelExport} disabled={isExporting}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                      </svg>
                      Export as Excel ({totalRecords} trainers)
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Trainer Table */}
          <div className="upt-master-table-wrap">
            <div className="common-table-container tb-bordered mt15">
              <CommonTable tableData={tableData} tablePagination={tablePagination} tableColumns={columns} isPage={isPage} setIsPage={setIsPage} pageLimit={pageLimit} setPageLimit={(val) => { setPageLimit(val); setIsPage(0); }} isLoading={isLoading} stickyColumns={2} />
            </div>
          </div>
        </>
      )}

      {/* Member Detail Modal */}
      <Modal
        isOpen={!!selectedMember}
        onRequestClose={closeMemberModal}
        contentLabel="Member Profile"
        ariaHideApp={false}
      >
        <div className="createNewCTOModalContainer mod-width-95vw mod-height-95vh">
          <ModalHeadAndClose
            title="Member Profile"
            onClose={closeMemberModal}
          />
          {selectedMember && (
            <UserProfile userData={selectedMember} />
          )}
        </div>
      </Modal>

    </>
  );
};

export default UnifiedPTDashboard;
